/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {GenericObject} from '@ion/core/src/types/entities';
import {dateFilterComparator, dateTimeISOFilterComparator} from '@ion/core/src/utils/SortUtils';
import {stringToNumber} from '@ion/core/src/utils/StringUtil';
import {clientGridDateFilterComparator} from '../utils/ComponentUtils';
import {SelectedRows} from './IonGridUtils';

export interface ColumnDefinitionType {
  [key: string]: any;
}

interface GetColumnDefinitionsTypes {
  isPhone?: boolean
  checkBoxColumnDefinition?: null | ColumnDefinitionType;
  columnDefinitions: any;
  checkboxesEnabled?: boolean;
  rowMenusEnabled?: boolean;
  secondaryRowMenusEnabled?: boolean;
  ctaProps?: GenericObject;
  secondaryCtaProps?: GenericObject;
}

interface CheckboxProps extends SelectedRows {
  pageSize: number;
  enableSelectAll: boolean;
  isClientModel?: boolean;
}

// Creates the checkbox header column definition with access to needed props.
export const getCheckboxColumn = ({
  totalRowCount,
  pageSize,
  enableSelectAll,
  isClientModel,
  setAreTotalRowsSelected
}: CheckboxProps): ColumnDefinitionType => ({
  ...checkBoxColumn,
  totalRowCount,
  pageSize,
  headerComponentParams : ({api}) => ({
    gridApi : api,
    totalRowCount,
    enableSelectAll,
    isClientModel,
    pageSize,
    setAreTotalRowsSelected
  })
});

/**
 * The checkbox column definition for the Ion Grid
 */
export const checkBoxColumn: ColumnDefinitionType = {
  headerName                          : '',
  field                               : 'checkBoxSelection',
  pinned                              : 'left',
  width                               : 50,
  minWidth                            : 50,
  maxWidth                            : 50,
  includeInCompare                    : true,
  lockPosition                        : true,
  headerCheckboxSelectionFilteredOnly : true,
  checkboxSelection                   : true,
  suppressSorting                     : true,
  suppressMovable                     : true,
  filter                              : false,
  sortable                            : false,
  headerComponent                     : 'headerCheckbox',
  headerComponentParams               : ({api}) => ({
    gridApi : api
  }),
  cellStyle : {paddingLeft: '14px', paddingRight: '32px'}
};

/**
 * @getActionsColumnDefinition
 *
 * returns the column definition for the end of row actions column cells
 */
export const getActionsColumnDefinition = ({
  id = 'id',
  width = 60,
  ctaHeaderLabel = '',
  columnAggregator = null,
  suppressMovable = true,
  lockPosition = true,
  rowDrag = false,
  onViewAuditChanges = null,
  ...rest
}: GenericObject): ColumnDefinitionType['desktop'] => ({
  headerName                : ctaHeaderLabel,
  field                     : id,
  cellRenderer              : 'rowMenu',
  width                     : width,
  minWidth                  : width,
  maxWidth                  : width,
  suppressMovable           : suppressMovable,
  floatingFilter            : false,
  pinned                    : 'right',
  lockPosition              : lockPosition,
  lockPinned                : lockPosition,
  rowDrag                   : rowDrag,
  filter                    : false,
  menuTabs                  : [],
  sortable                  : false,
  cellRendererParams        : {id, onViewAuditChanges},
  aggFunc                   : columnAggregator,
  suppressHeaderMenuButton  : true,
  suppressHeaderContextMenu : true,
  ...rest
});

/**
 * @getSecondaryActionsColumnDefinition
 *
 * returns the column definition for a secondary end of row actions column cells that
 * gets rendered after the original actions column
 */
export const getSecondaryActionsColumnDefinition = ({
  id = 'id',
  width = 60,
  ctaHeaderLabel = '',
  columnAggregator = null,
  suppressMovable = true,
  lockPosition = true,
  rowDrag = false,
  ...rest
}: GenericObject): ColumnDefinitionType['desktop'] => ({
  headerName                : ctaHeaderLabel,
  field                     : id,
  cellRenderer              : 'secondaryRowMenu',
  width                     : width,
  minWidth                  : width,
  maxWidth                  : width,
  suppressMovable           : suppressMovable,
  floatingFilter            : false,
  pinned                    : 'right',
  lockPosition              : lockPosition,
  lockPinned                : lockPosition,
  rowDrag                   : rowDrag,
  filter                    : false,
  menuTabs                  : [],
  sortable                  : false,
  cellRendererParams        : {id},
  aggFunc                   : columnAggregator,
  suppressHeaderMenuButton  : true,
  suppressHeaderContextMenu : true,
  ...rest
});

/**
 * @getColumnDefinitions
 *
 * Function that puts together all the definitions into a consumable
 * column set for Ion Grid. Takes column definitions as an array defaulted as
 * the column defs or if they are split up will render them conditionally on the
 * mobile screen hook. Optional chaining allows us to look for a property and if it's
 * an Array it'll just return undefined.
 */
export const getColumnDefinitions = ({
  columnDefinitions,
  checkboxesEnabled,
  rowMenusEnabled,
  secondaryRowMenusEnabled = false,
  checkBoxColumnDefinition = null,
  isPhone = false,
  ctaProps = {},
  secondaryCtaProps = {}
}: GetColumnDefinitionsTypes) => {
  const columns = checkBoxColumnDefinition && checkboxesEnabled ? [checkBoxColumnDefinition] : [];
  const definitions = isPhone ? columnDefinitions?.mobile : columnDefinitions?.desktop;
  return (
    columns.concat(definitions || columnDefinitions)
  ).concat(
    rowMenusEnabled ? getActionsColumnDefinition(ctaProps) : []
  ).concat(
    secondaryRowMenusEnabled ? getSecondaryActionsColumnDefinition(secondaryCtaProps) : []
  );
};

/**
 * Include / Exclude Column filtering
 */
export const filterIncludeExclude = (_filterValue: any, cellValue: any, filter: string) => {
  return filter === 'Exclude' ? cellValue === null : cellValue !== null;
};

/**
 * default filter params for text filters
 */
export const defaultTextFilterParams = {
  suppressAndOrCondition : true,
  filterOptions          : ['equals', 'notEqual', 'contains', 'startsWith', 'endsWith'],
  defaultOption          : 'contains',
  buttons                : ['clear', 'apply'],
  caseSensitive          : true
};

/**
 * case insensitive filter params for text filters
 */
export const caseInSensitiveDefaultTextFilterParams = {
  ...defaultTextFilterParams,
  caseSensitive : false
};

/**
 * default filter params for CVS text filters
 */
export const defaultCVSFilterParams = {
  ...defaultTextFilterParams,
  caseSensitive : false,
  filterOptions : ['equals', 'contains', 'startsWith', 'endsWith']
};

/**
 * default filter params for only equals
 */
export const defaultEqualsFilterParams = {
  suppressAndOrCondition : true,
  filterOptions          : ['equals'],
  defaultOption          : 'equals',
  buttons                : ['clear', 'apply'],
  caseSensitive          : true
};

/**
 * filter params for only equals with case insensitive
 */
export const caseInSensitiveEqualsFilterParams = {
  ...defaultEqualsFilterParams,
  caseSensitive : false
};

/**
 * default filter params for status filters
 */
export const defaultStatusFilterParams = {
  suppressAndOrCondition : true,
  filterOptions          : ['equals', 'notEqual'],
  defaultOption          : 'equals',
  buttons                : ['clear', 'apply'],
  caseSensitive          : true
};

/**
 * default filter params for date filters
 */
export const defaultDateFilterParams = {
  suppressAndOrCondition : true,
  filterOptions          : ['equals', 'inRange', 'greaterThan', 'lessThan'],
  defaultOption          : 'equals',
  buttons                : ['clear', 'apply'],
  inRangeInclusive       : true,
  comparator             : dateFilterComparator
};

//filter params for date filters
export const ISODateFilterParams  = {
  ...defaultDateFilterParams,
  comparator : dateTimeISOFilterComparator
};

//filter params for client grid date filters
export const ClientISODateFilterParams = {
  ...defaultDateFilterParams,
  comparator : clientGridDateFilterComparator
};

/**
 * default filter params for number filters
 */
export const defaultNumberFilterParams = {
  suppressAndOrCondition : true,
  filterOptions          : ['equals', 'lessThan', 'greaterThan'],
  defaultOption          : 'equals',
  buttons                : ['clear', 'apply'],
  allowedCharPattern     : '\\-\\d\\.\\,',
  numberParser           : (val: string) => stringToNumber(val)
};

/**
 * default filter params for coma separated texts
 */
export const defaultTextContainsFilterParams = {
  suppressAndOrCondition : true,
  filterOptions          : ['contains'],
  defaultOption          : 'contains',
  buttons                : ['clear', 'apply'],
  caseSensitive          : true
};

/**
 * default filter params for Include / Exclude Column filtering
 */
export const includeExcludeFilterParms = {
  suppressAndOrCondition : true,
  filterOptions          : [
    'empty',
    {
      displayKey  : 'Include',
      displayName : 'Include',
      predicate   : ([filterValue]: any, cellValue: any) => filterIncludeExclude(
        filterValue,
        cellValue,
        'Include'),
      hideFilterInput : true,
      numberOfInputs  : 0
    },
    {
      displayKey  : 'Exclude',
      displayName : 'Exclude',
      predicate   : ([filterValue]: any, cellValue: any) => filterIncludeExclude(
        filterValue,
        cellValue,
        'Exclude'),
      hideFilterInput : true,
      numberOfInputs  : 0
    }
  ],
  buttons : ['clear', 'apply']
};

// default cell renderer params...
export const onDefaultCellParams = ({data}) => ({data});
